import React, { ReactNode, useCallback, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import classNames from "classnames";
import { Card } from "@ui/card";
import { animated, useSpring, config } from "@react-spring/web";
import { HoverableChevron } from "@ui/hoverable-chevron";

export interface ExpandableCardProps {
  topContent: ReactNode;
  bottomContent: ReactNode;
  bottomContentClass?: string;
  expandedHeight: number;
  onExpand?: (userTouched: boolean) => void;
  onCollapse?: () => void;
  defaultExpanded?: boolean;
}

const useStyles = makeStyles({
  hideScrollbar: {
    scrollbarWidth: "none" /* Firefox */,
    msOverflowStyle: "none" /* IE 10+ */,
    "&::-webkit-scrollbar": {
      display: "none" /* Safari and Chrome */,
    },
  },
});

export const ExpandableCard = ({
  topContent,
  bottomContent,
  bottomContentClass,
  expandedHeight,
  onExpand,
  onCollapse,
  defaultExpanded = false,
}: ExpandableCardProps) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [touched, setTouched] = useState(false);

  const onToggleExpanded = useCallback(() => {
    setExpanded(!expanded);
    setTouched(true);
  }, [expanded]);

  const [{ maxHeight }] = useSpring(
    {
      maxHeight: expanded ? expandedHeight : 0,
      config: config.default,
    },
    [expanded],
  );

  React.useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  React.useEffect(() => {
    if (expanded) {
      onCollapse?.();
    }
    if (!expanded) {
      onExpand?.(touched);
    }
  }, [expanded, onExpand, onCollapse]);

  return (
    <animated.div>
      <Card className="cw-p-0">
        <div
          className="cw-px-3 cw-min-h-[46px] cw-flex cw-flex-col cw-justify-center cw-cursor-pointer"
          onClick={onToggleExpanded}
        >
          <div className="cw-flex cw-flex-row cw-justify-between">
            {topContent}
            <HoverableChevron direction={expanded ? "up" : "down"} />
          </div>
        </div>
        <animated.div style={{ maxHeight }} className="cw-flex cw-flex-col">
          <div
            className={classNames(
              bottomContentClass,
              classes.hideScrollbar,
              "cw-m-0 cw-px-3 cw-overflow-x-hidden cw-overflow-y-auto",
            )}
          >
            {bottomContent}
          </div>
        </animated.div>
      </Card>
    </animated.div>
  );
};
